import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { getAnnotationStatusText } from '../../../../../../../modules/annotations/getAnnotationStatusText';
import { FilterPill } from '../../components/filters/FilterPill';
import { AnnotationToolFilterType } from '../../imageFilters.model';
import { IProjectDetailsImagesStore } from '../../projectDetailsImages.store';
import { IImageFilterService } from '../../services/imageFilters.service';

const INFINITY_SYMBOLS = ['-∞', '+∞'];

export const PillsContainer = observer((): ReactElement => {
  const imageFilterService = useInjection(IImageFilterService.$);
  const imagesStore = useInjection(IProjectDetailsImagesStore.$);

  const { t } = useTranslator();

  const handleClearAnnotatedByAsync = async (): Promise<void> => {
    await imageFilterService.clearAnnotatedByAsync();
  };

  const handleClearAnnotationToolsAsync = async (): Promise<void> => {
    await imageFilterService.clearAnnotationToolsAsync();
  };

  const handleClearCorrectedAsync = async (): Promise<void> => {
    await imageFilterService.clearCorrectedAsync();
  };

  const handleClearImageSet = async (): Promise<void> => {
    await imageFilterService.clearImageSetsAsync();
  };

  const handleClearDatasets = async (): Promise<void> => {
    await imageFilterService.clearInDatasetsAsync();
  };

  const handleClearReviewedBy = async (): Promise<void> => {
    await imageFilterService.clearReviewedByAsync();
  };

  const handleClearStatus = async (): Promise<void> => {
    await imageFilterService.clearStatusesAsync();
  };

  const getToolLines = (): string[] => {
    const toolMap = new Map<string, string>();
    const toolLines: string[] = [];

    availableFilters.annotationTypes.forEach((a) => {
      toolMap.set(a.id, a.name);
      a.attributes.forEach((b) => {
        toolMap.set(b.id, b.text);
        b.possibleAnswers.forEach((c) => {
          toolMap.set(c.id, c.text);
        });
      });
    });

    availableFilters.questionFilters.forEach((q) => {
      toolMap.set(q.id, q.text);
      q.possibleAnswers.forEach((c) => {
        toolMap.set(c.id, c.text);
      });
    });

    imageFilters.annotationTools.forEach((a) => {
      const texts: (string | undefined)[] = [];

      texts.push(toolMap.get(a.l1Filter ?? ''));
      texts.push(toolMap.get(a.l2Filter ?? ''));

      const isNumber = a.l3FilterType === AnnotationToolFilterType.AttributeAnswerNumber || a.l3FilterType === AnnotationToolFilterType.QuestionAnswerNumber;

      const values = a.l3Filters.map((l, i) => {
        if (!l && isNumber) {
          return INFINITY_SYMBOLS[i];
        }

        return toolMap.get(l) ?? l;
      });

      if (values.length) {
        texts.push(values.join(', '));
      }

      toolLines.push(texts.filter((x) => x).join(' > '));
    });

    return toolLines;
  };

  const { imageFilters, availableFilters } = imagesStore;

  return (
    <div className='image-filters-pills'>
      <FilterPill
        count={imageFilters.datasets.length}
        labelKey={'datasets'}
        onClear={handleClearDatasets}
        titleEnties={availableFilters.datasets.filter((x) => imageFilters.datasets.includes(x.id)).map((x) => x.name)}
      />
      <FilterPill
        titleEnties={availableFilters.annotatedBy.filter((x) => imageFilters.annotatedBy.includes(x.id)).map((x) => x.email)}
        count={imageFilters.annotatedBy.length}
        labelKey={'annotated_by'}
        onClear={handleClearAnnotatedByAsync}
      />
      <FilterPill
        titleEnties={availableFilters.reviewedBy.filter((x) => imageFilters.reviewedBy.includes(x.id)).map((x) => x.email)}
        count={imageFilters.reviewedBy.length}
        labelKey={'reviewed_by'}
        onClear={handleClearReviewedBy}
      />
      <FilterPill
        titleEnties={imageFilters.annotationStatuses.map((d) => t(getAnnotationStatusText(d)))}
        count={imageFilters.annotationStatuses.length}
        labelKey={'status'}
        onClear={handleClearStatus}
      />
      <FilterPill
        titleEnties={getToolLines()}
        count={imageFilters.annotationTools.filter((x) => x.l1FilterType !== undefined).length}
        labelKey={'annotation_tools'}
        onClear={handleClearAnnotationToolsAsync}
      />
      <FilterPill
        titleEnties={imageFilters.corrected.map((x) => (x ? t('yes') : t('no')))}
        count={imageFilters.corrected.length}
        labelKey={'corrected'}
        onClear={handleClearCorrectedAsync}
      />
      <FilterPill
        titleEnties={imageFilters.imageSets.map((s) => t(`image_set_options.${s}`))}
        count={imageFilters.imageSets.length}
        labelKey={'image_set'}
        onClear={handleClearImageSet}
      />
    </div>
  );
});
